import { HTMLAttributes, PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from '@sib/i18n';
import { cn } from '../utils';
import { Button, ButtonProps } from './Button';
import { DialogClose } from './Dialog';

type ActionButtonProps = ButtonProps & { label?: ReactNode };

interface ConfirmationDialogContentProps extends PropsWithChildren {
  onCancel: () => void;
  onConfirm: () => void;
  title?: ReactNode;
  description?: ReactNode;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  TitleProps?: HTMLAttributes<HTMLHeadingElement>;
  DescriptionProps?: HTMLAttributes<HTMLParagraphElement>;
  FooterProps?: HTMLAttributes<HTMLDivElement>;
  CancelButtonProps?: ActionButtonProps;
  ConfirmButtonProps?: ActionButtonProps;
  cancelUsesDialogClose?: boolean;
}

const ConfirmationDialogContent = ({
  onCancel,
  onConfirm,
  title,
  description,
  children,
  loading = false,
  disabled = false,
  className,
  TitleProps,
  DescriptionProps,
  FooterProps,
  CancelButtonProps = {},
  ConfirmButtonProps = {},
  cancelUsesDialogClose = false,
}: ConfirmationDialogContentProps) => {
  const { t } = useTranslation();
  const { label: cancelLabel, ...cancelButtonProps } = CancelButtonProps;
  const {
    label: confirmLabel,
    disabled: confirmButtonDisabled,
    ...confirmButtonProps
  } = ConfirmButtonProps;
  const isConfirmDisabled =
    disabled || loading || Boolean(confirmButtonDisabled);

  const cancelButton = (
    <Button
      type="button"
      disabled={loading}
      variant="outline"
      {...(!cancelUsesDialogClose && { onClick: onCancel })}
      {...cancelButtonProps}
    >
      {cancelLabel ?? t('cancel')}
    </Button>
  );

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-7 text-center',
        className
      )}
    >
      {title &&
        (typeof title === 'string' ? (
          <h4
            {...TitleProps}
            className={cn('text-primary', TitleProps?.className)}
          >
            {title}
          </h4>
        ) : (
          title
        ))}
      {description &&
        (typeof description === 'string' ? (
          <p
            {...DescriptionProps}
            className={cn('text-primary text-sm', DescriptionProps?.className)}
          >
            {description}
          </p>
        ) : (
          description
        ))}
      {children}
      <div
        {...FooterProps}
        className={cn('flex justify-center gap-4', FooterProps?.className)}
      >
        {cancelUsesDialogClose ? (
          <DialogClose asChild>{cancelButton}</DialogClose>
        ) : (
          cancelButton
        )}
        <Button
          {...confirmButtonProps}
          disabled={isConfirmDisabled}
          loading={loading}
          onClick={onConfirm}
        >
          {confirmLabel ?? t('confirm')}
        </Button>
      </div>
    </div>
  );
};

export { ConfirmationDialogContent };
export type { ConfirmationDialogContentProps, ActionButtonProps };
